import { mask } from 'maska'
import type { SheetColumnCustomEditor } from './attendanceSheet.types'
import { parsePastedTime } from './sheet-paste'

/**
 * Editor de célula de HORA da folha de monitoramento (mecanismo nativo `CustomEditor` do
 * jspreadsheet CE). Fecha o gap deixado em #2114, quando `time` foi mapeado para `text` puro:
 * a célula aceitava letras, não tinha máscara e o lixo digitado chegava inteiro ao payload de
 * conciliação — a validação só existia no caminho da colagem (RN-08).
 *
 * Vive no design system, não em `DsAttendance/utils/` — mesma razão de `sheet-paste.ts` (ver
 * seu cabeçalho): quem consome é `AttendanceSheetCard.vue` (`Components`), e a direção de
 * camadas de `.claude/rules/architecture.md` (`Modules -> Components`) proíbe um `Component`
 * importar de um `Module`. Além disso `time` é um `ColumnType` do próprio design system: o
 * editor precisa valer para qualquer consumidor, não só para a tela de auditoria.
 *
 * Só a engine `spreadsheet` chega aqui: `toGridColumn` roda dentro de `createGrid`, que
 * retorna cedo quando `engine !== 'spreadsheet'`. A engine `native` (flag desligada) renderiza
 * `<input type="time">` do browser, que já mascara e valida por conta própria.
 *
 * Ao contrário do editor de CRM/nome (`doctor-suggestion-cell-editor.ts`), aqui não há app
 * Vue: um `<input>` de DOM puro basta, e sendo ele o `children[0]` do `<td>` ainda ganha de
 * graça o fechamento por Enter/Tab do jspreadsheet, que chama `edition[0].children[0].blur()`
 * (`keyDownControls`, dist da lib) — daí o listener de `blur` abaixo.
 */

/**
 * Máscara aplicada como FUNÇÃO PURA da `maska`, nunca pela diretiva global `v-maska`
 * (#2289): a diretiva grava a máscara no atributo `data-mask` do input, e o `jsuites`
 * (trazido pelo `jspreadsheet-ce`) sequestra qualquer input com esse atributo, reescrevendo
 * o valor com o próprio motor e imprimindo `##:##` literal na tela. Ver `src/directives/vMask.ts`.
 */
const TIME_MASK = '##:##'

/** `HH:MM` — o input nunca precisa aceitar mais que a máscara completa. */
const TIME_MAX_LENGTH = 5

/**
 * Valor com que o input abre a partir do que está salvo na célula.
 *
 * `parsePastedTime` vem ANTES da máscara de propósito: a máscara é uma reposição posicional
 * de dígitos, não um parser — ela joga fora o `:` e reencaixa o resto, então `7:00` (hora
 * válida pela RN-08, gravável por colagem ou vinda do backend) viraria `70:0` e o auditor
 * abriria a célula vendo lixo. Parseando primeiro, `7:00` abre já normalizado como `07:00`;
 * o que não parseia (dado legado, texto solto de antes desta correção) cai na máscara, que
 * salva os dígitos aproveitáveis e descarta o resto.
 */
function seedFromSavedValue(value: unknown): string {
  const saved = String(value ?? '')
  return parsePastedTime(saved) ?? mask(saved, TIME_MASK)
}

export function createSheetTimeCellEditor(): SheetColumnCustomEditor {
  /**
   * Célula que o input vivo está editando. Existe para a guarda de reentrância do
   * `openEditor` (N8/#2359): a lib reinvoca o hook durante a digitação, e sem saber *qual*
   * célula está em edição não há como distinguir "reabriram a mesma" de "pularam para outra".
   */
  let activeCell: HTMLTableCellElement | null = null
  let activeInput: HTMLInputElement | null = null
  /**
   * Último valor mascarado. É o que `closeEditor` valida — e não `activeInput.value` — porque
   * o `<td>` pode já ter sido re-renderizado pela lib quando o hook roda.
   */
  let currentValue = ''

  function teardown() {
    activeCell = null
    activeInput = null
    currentValue = ''
  }

  return {
    openEditor(cell, value, x, y, instance, _columnConfig, triggerEvent) {
      // N8/#2359 — guarda de reentrância, portada do editor de CRM/nome: a lib reinvoca
      // `openEditor` com a edição em curso, e sem esta guarda todo o corpo abaixo reexecutava
      // a cada tecla, devolvendo o input ao valor de abertura (`''` no caminho type-to-open).
      // A perda era proporcional à velocidade de digitação — foi assim que `99999-ZZ` chegou
      // como `ZZ` na coluna de CRM (UAT-16/21/24).
      if (activeInput) {
        if (cell === activeCell) return
        teardown()
      }

      // UAT-26 — a lib não repassa um flag de "abrir em branco"; o 7º argumento (o evento que
      // disparou a edição) é o único sinal disponível, e F2, Espaço e digitação de caractere
      // chegam todos como `KeyboardEvent`. Só a digitação de um caractere deve começar vazia,
      // do contrário o dígito digitado concatena com a hora já salva. Espaço é excluído
      // (`key === ' '` também tem `length === 1`) porque a lib o trata como "abrir
      // preservando"; `key === 'Process'` cobre o keydown de composição IME/CJK, que a própria
      // lib classifica como digitação.
      const openedByTypingCharacter =
        triggerEvent instanceof KeyboardEvent &&
        ((triggerEvent.key.length === 1 && triggerEvent.key !== ' ') ||
          triggerEvent.key === 'Process')
      currentValue = openedByTypingCharacter ? '' : seedFromSavedValue(value)

      cell.innerHTML = ''
      // O `.editor` é o que o CSS da lib usa para soltar o `<td>` do layout normal — e também
      // o que o helper de E2E `preencherCelulaTexto` procura (`td.editor input`). A lib só
      // adiciona a classe sozinha nos editores nativos; com `type` objeto, é por nossa conta.
      cell.classList.add('editor')

      const input = document.createElement('input')
      input.type = 'text'
      // `numeric` traz o teclado numérico no mobile sem virar `type="number"` (que rejeitaria
      // o `:` da máscara e ainda mostraria os botões de incremento).
      input.inputMode = 'numeric'
      input.maxLength = TIME_MAX_LENGTH
      input.autocomplete = 'off'
      input.value = currentValue
      input.setAttribute(
        'data-testid',
        `sheet-cell-input-${String(instance.options.columns?.[x]?.name ?? x)}-${y}`
      )
      // Mesmo dimensionamento que a lib dá ao editor de texto nativo (`openEditor` no dist):
      // a célula de hora continua com a aparência de hoje, quando a coluna ainda era `text`.
      const rect = cell.getBoundingClientRect()
      input.style.width = `${rect.width}px`
      input.style.height = `${rect.height - 2}px`
      input.style.minHeight = `${rect.height - 2}px`

      input.addEventListener('input', () => {
        // `mask()` descarta tudo que não casa com `##`: letras e símbolos simplesmente não
        // entram, e o `:` aparece sozinho ao completar a hora.
        currentValue = mask(input.value, TIME_MASK)
        input.value = currentValue
        // Caret sempre no fim: são 5 caracteres e o caso dominante é digitar da esquerda para
        // a direita. O `fixInputSelection` de `vMask.ts` (porte da `maska`) resolve edição no
        // meio de campos longos — complexidade sem contrapartida aqui.
        input.setSelectionRange(currentValue.length, currentValue.length)
      })

      // Enter e Tab não chamam `closeEditor`: a lib chama `children[0].blur()` e conta com o
      // `onblur` que ela mesma instala nos editores nativos. Com `type` objeto esse `onblur`
      // não existe, então é aqui que a edição vira commit. A guarda evita fechar duas vezes
      // quando o blur é consequência da própria remoção do input pelo `closeEditor` da lib.
      input.addEventListener('blur', () => {
        if (activeInput !== input) return
        instance.closeEditor(cell, true)
      })

      cell.appendChild(input)
      activeCell = cell
      activeInput = input
      input.focus()
    },
    // `updateCell` — sem este hook, toda escrita fora de `closeEditor(save=true)`
    // (`setValue`/`setValueFromCoords`/`undo`/`redo`, ou seja: colagem, Ctrl+Z e "Aplicar à
    // coluna") só atualizaria `options.data`, nunca o DOM: a lib escreve a célula sozinha
    // enquanto o `type` é string, mas ao virar objeto passa a delegar essa escrita ao editor.
    // Foi exatamente a regressão B2 do editor de CRM/nome. Retornar `undefined` mantém o
    // valor recebido — este hook só pinta.
    updateCell(cell, value) {
      cell.textContent = String(value ?? '')
      return undefined
    },
    closeEditor(cell, save, x, y, instance) {
      const value = currentValue
      teardown()

      if (!save) {
        // Escape — devolve a célula ao que já estava salvo. A lib não re-renderiza sozinha
        // uma coluna com editor customizado (mesmo motivo do `doctor-suggestion-cell-editor`).
        cell.textContent = String(instance.options.data?.[y]?.[x] ?? '')
        return undefined
      }

      const text = value.trim()
      // Limpar a célula é legítimo: apagar uma hora não é erro de digitação.
      if (text === '') return ''

      // RN-08 — `parsePastedTime` é a definição canônica de hora válida do domínio (aceita
      // `HH:MM`, `H:MM` e `HH:MM:SS`, normaliza para `HH:MM`, exige hora em 0–23). Reusada de
      // propósito: a máscara garante o FORMATO, não a FAIXA — `99:99` passa por ela intacto.
      // O que não parseia é DESCARTADO (célula vazia), a mesma regra que a colagem já aplica.
      // Sem toast nem UI nova: se a linha estiver em uso, `classifyRow` a marca como `partial`
      // e a borda vermelha com o motivo no rodapé já aparecem.
      return parsePastedTime(text) ?? ''
    }
  }
}
